<template>
  <!-- Item (a), batch 3: shown before saving a task or event when an automatic follow-up email is due around it -->
  <Dialog :visible.sync="shown" header="Automatic follow-up email due" :modal="true" :style="{ width: '640px' }"
    :contentStyle="{ maxHeight: '60vh', overflowY: 'auto' }" id="followupDueWarning">
    <p class="p-mt-0">The customer is due an automatic follow-up email around this {{ noun }}. What should happen to it?</p>
    <div v-for="row in rows" :key="row.followupId" class="due-row p-mb-3">
      <div class="due-line">{{ line(row) }}<span v-if="row.stopped"> - stopped</span><span v-else-if="row.skipped"> - will be skipped</span></div>
      <div class="p-d-flex p-flex-wrap p-mt-1">
        <Button :id="'keep' + row.followupId" label="Keep" class="p-button-sm p-mr-2"
          :class="row.skipped || row.stopped ? 'p-button-outlined' : ''" :disabled="busy || row.stopped || !row.canAct" @click="keep(row)" />
        <Button :id="'skip' + row.followupId" label="Skip this one" class="p-button-sm p-mr-2"
          :class="row.skipped && !row.stopped ? '' : 'p-button-outlined'" :disabled="busy || row.stopped || !row.canAct" @click="skip(row)" />
        <Button :id="'stop' + row.followupId" label="Stop all for this quote" class="p-button-sm p-button-danger"
          :class="row.stopped ? '' : 'p-button-outlined'" :disabled="busy || row.stopped || !row.canAct" @click="stop(row)" />
      </div>
      <small v-if="!row.canAct" class="hint">Only {{ row.salespersonName || 'the salesperson' }} or an admin can change it.</small>
    </div>
    <small v-if="problem" class="p-invalid">{{ problem }}</small>
    <template #footer>
      <Button label="Go back" icon="pi pi-times" class="p-button-text" @click="finish(false)" />
      <Button id="saveAfterWarning" :label="'Save the ' + noun" icon="pi pi-check" :disabled="busy" @click="finish(true)" />
    </template>
  </Dialog>
</template>

<script>
import FollowupService, { errorMessage } from '@/service/FollowupService'
import { warningQuery, hasTarget, warningLine } from './followupWarning'
import { FOLLOWUP_EMAILS_ON } from '@/featureFlags'

export default {
  name: 'FollowupDueWarning',
  props: {
    noun: { type: String, default: 'task' }
  },
  data: () => ({ followupService: new FollowupService(), rows: [], visible: false, busy: false, problem: '', resolveFn: null }),
  computed: {
    shown: {
      get () { return this.visible },
      set (v) { if (!v) this.finish(false) }
    }
  },
  methods: {
    line: warningLine,
    /**
     * Resolves true to go on saving, false to go back. Nothing due (or the check fails): true at once, so a follow-up
     * problem never blocks saving a task or event.
     */
    check (record) {
      if (!FOLLOWUP_EMAILS_ON) return Promise.resolve(true) // stage 1: follow-up emails off (featureFlags.js)
      var q = warningQuery(record)
      if (!hasTarget(q)) return Promise.resolve(true)
      return this.followupService.dueFor(q)
        .then(rows => {
          if (!rows || !rows.length) return true
          this.rows = rows.map(r => ({ ...r, stopped: false }))
          this.problem = ''
          this.visible = true
          return new Promise(resolve => { this.resolveFn = resolve })
        })
        .catch(() => true)
    },
    act (row, call, after) {
      this.busy = true
      this.problem = ''
      return call(row.followupId)
        .then(after)
        .catch(err => { this.problem = errorMessage(err, 'That could not be changed.') })
        .finally(() => { this.busy = false })
    },
    keep (row) {
      if (!row.skipped) return Promise.resolve()
      return this.act(row, id => this.followupService.unskip(id), () => { row.skipped = false })
    },
    skip (row) {
      if (row.skipped) return Promise.resolve()
      return this.act(row, id => this.followupService.skipNext(id, row.stepNo), () => { row.skipped = true })
    },
    stop (row) {
      return this.act(row, id => this.followupService.stop(id), () => { row.stopped = true })
    },
    finish (ok) {
      this.visible = false
      var r = this.resolveFn
      this.resolveFn = null
      if (r) r(ok)
    }
  }
}
</script>

<style scoped>
.due-row { border-bottom: 1px solid #e9ecef; padding-bottom: 0.5rem; }
.due-line { font-weight: 600; }
.hint { color: #6c757d; }
</style>
